/**
 * 跨窗口 Auth 同步模块（租户端）
 *
 * 通过 postMessage 与采购商城 (web-mall) 安全地同步认证状态：
 * - 打开商城时通过 postMessage 传递 token（不再拼 URL）
 * - 登出时通知商城清理 token
 * - token 刷新后通知商城更新
 */

import { getToken, getRefreshToken } from './storage';

// ─── 消息协议 ───────────────────────────────────────────────

export interface AuthMessage {
  type: 'AUTH_READY' | 'AUTH_TOKEN' | 'AUTH_LOGOUT' | 'AUTH_TOKEN_REFRESH';
  token?: string;
  refreshToken?: string;
  source: 'web' | 'web-mall';
}

// ─── Origin 白名单 ─────────────────────────────────────────

const CONFIGURED_MALL_URL = import.meta.env.VITE_MALL_URL as string | undefined; // e.g. 'http://localhost:5174' or '/mall/'

type RuntimeLocation = Pick<Location, 'hostname' | 'origin'>;

/**
 * Resolve the mall URL at runtime.
 *
 * The test and staging deployments can share the same staging-mode web image,
 * so a build-time VITE_MALL_URL may point at staging even when the image is
 * running on test.pipexerp.com. Runtime host mapping prevents cross-env jumps.
 * A relative VITE_MALL_URL (staging: '/mall/') follows whatever domain the
 * admin is opened on, so tenant custom domains land on their own mall.
 */
export function resolveMallUrl(
  configuredUrl = CONFIGURED_MALL_URL,
  runtimeLocation: RuntimeLocation | undefined = typeof window !== 'undefined' ? window.location : undefined,
): string | undefined {
  const hostname = runtimeLocation?.hostname;

  if (hostname === 'test.pipexerp.com') {
    return 'https://test.emall.pipexerp.com';
  }

  if (hostname === 'pipexerp.com' || hostname === 'www.pipexerp.com') {
    return 'https://emall.pipexerp.com';
  }

  if (!configuredUrl || !runtimeLocation) return configuredUrl;
  return new URL(configuredUrl, runtimeLocation.origin).toString();
}

function getAllowedOrigins(): string[] {
  const origins: string[] = [];
  const mallUrl = resolveMallUrl();
  if (mallUrl) {
    // 取 origin 部分（去掉尾部斜杠和路径）
    try {
      origins.push(new URL(mallUrl).origin);
    } catch {
      origins.push(mallUrl.replace(/\/+$/, ''));
    }
  }
  return origins;
}

function isAllowedOrigin(origin: string): boolean {
  return getAllowedOrigins().includes(origin);
}

// ─── 窗口引用管理 ───────────────────────────────────────────

let mallWindowRef: Window | null = null;

/**
 * 安全打开采购商城，通过 postMessage 传递 token
 * 流程：
 *  1. 打开商城窗口（不附带 token 参数）
 *  2. 商城加载完成后发送 AUTH_READY
 *  3. 租户端收到 AUTH_READY 后回传 AUTH_TOKEN
 */
export function openMallSecure(mallUrl = resolveMallUrl()): void {
  if (!mallUrl) return;

  // 复用已有窗口（如果还未关闭）
  if (mallWindowRef && !mallWindowRef.closed) {
    mallWindowRef.focus();
    sendTokenToMall();
    return;
  }
  mallWindowRef = window.open(mallUrl, 'coolbuy-mall');
  // AUTH_READY 是主路径；这里延迟补发一次，覆盖商城窗口已打开但 ready 消息丢失的情况。
  window.setTimeout(sendTokenToMall, 300);
}

/**
 * 向商城窗口发送 token
 */
function sendTokenToMall(): void {
  const target = mallWindowRef;
  const targetOrigin = getAllowedOrigins()[0];
  if (!target || target.closed || !targetOrigin) return;

  const token = getToken();
  if (!token) return;

  const msg: AuthMessage = {
    type: 'AUTH_TOKEN',
    token,
    refreshToken: getRefreshToken() || undefined,
    source: 'web',
  };
  target.postMessage(msg, targetOrigin);
}

/**
 * 向商城窗口发送登出通知
 */
export function sendLogoutToMall(): void {
  const target = mallWindowRef;
  const targetOrigin = getAllowedOrigins()[0];
  if (!target || target.closed || !targetOrigin) return;

  const msg: AuthMessage = { type: 'AUTH_LOGOUT', source: 'web' };
  target.postMessage(msg, targetOrigin);
}

/**
 * 向商城窗口发送 token 刷新通知
 */
export function sendTokenRefreshToMall(newToken: string, newRefreshToken?: string): void {
  const target = mallWindowRef;
  const targetOrigin = getAllowedOrigins()[0];
  if (!target || target.closed || !targetOrigin) return;

  const msg: AuthMessage = {
    type: 'AUTH_TOKEN_REFRESH',
    token: newToken,
    refreshToken: newRefreshToken,
    source: 'web',
  };
  target.postMessage(msg, targetOrigin);
}

// ─── 从商城 opener 请求 token（admin 作为子窗口时）─────────

function getOpener(): Window | null {
  try {
    return window.opener && !window.opener.closed ? window.opener : null;
  } catch {
    return null;
  }
}

/**
 * 向商城 opener 请求 token（mall→admin SSO 反向）
 * 当管理后台由商城 openAdminSecure() 打开时，发送 AUTH_READY 触发商城回传 AUTH_TOKEN
 */
export function requestTokenFromOpener(): void {
  const opener = getOpener();
  const targetOrigin = getAllowedOrigins()[0];
  if (!opener || !targetOrigin) return;

  const msg: AuthMessage = { type: 'AUTH_READY', source: 'web' };
  opener.postMessage(msg, targetOrigin);
}

// ─── 监听商城发来的消息 ─────────────────────────────────────

export interface AuthMessageCallbacks {
  onToken?: (token: string, refreshToken?: string) => void;
  onLogout?: () => void;
  onTokenRefresh?: (token: string, refreshToken?: string) => void;
}

/**
 * 监听来自商城的 postMessage 事件
 * 返回清理函数
 */
export function listenForAuthMessages(callbacks: AuthMessageCallbacks): () => void {
  const handler = (event: MessageEvent<AuthMessage>) => {
    if (!isAllowedOrigin(event.origin)) return;

    const { data } = event;
    if (!data || data.source !== 'web-mall') return;

    switch (data.type) {
      case 'AUTH_READY':
        // 商城已加载（admin→mall SSO），发送 token
        sendTokenToMall();
        break;

      case 'AUTH_TOKEN':
        // 商城回传 token（mall→admin SSO 反向）
        if (data.token) {
          callbacks.onToken?.(data.token, data.refreshToken);
        }
        break;

      case 'AUTH_LOGOUT':
        callbacks.onLogout?.();
        break;

      case 'AUTH_TOKEN_REFRESH':
        if (data.token) {
          callbacks.onTokenRefresh?.(data.token, data.refreshToken);
        }
        break;
    }
  };

  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
}
